
import React from "react";
import { Candidate } from "../candidate-types";
import { ExternalLink } from "lucide-react";

export const getTopCandidates = (candidatesPerRole: { [roleId: string]: Candidate[]; }, roleId: string) => {
  if (!candidatesPerRole[roleId]) {
    console.log(`[candidateUtils] No candidates found for role ${roleId}`);
    return { first: null, second: null, third: null };
  }
  
  // Apply consistent sorting: match percentage (desc), then candidate_id (asc)
  const sortedCandidates = candidatesPerRole[roleId]
    .slice() // Create a copy to avoid mutating original array
    .sort((a, b) => {
      if (a.matchPercentage !== b.matchPercentage) {
        return b.matchPercentage - a.matchPercentage; // Higher percentage first
      }
      
      // If match percentages are equal, sort by candidate_id ascending
      const aId = a.candidate_id || a.id || 0;
      const bId = b.candidate_id || b.id || 0;
      return Number(aId) - Number(bId);
    })
    .slice(0, 3); // Take top 3

  // Enhanced logging with data integrity checks
  console.log(`[candidateUtils] Processing ${candidatesPerRole[roleId].length} candidates for role ${roleId}`);
  console.log(`[candidateUtils] Top candidates for role ${roleId}:`, sortedCandidates.map(c => ({
    name: c.candidate_name,
    matchPercentage: c.matchPercentage,
    candidate_id: c.candidate_id || c.id,
    id: c.id,
    created_at: c.created_at || 'N/A'
  })));

  // Data integrity check - warn about potential duplicates
  const candidateIds = sortedCandidates.map(c => c.candidate_id || c.id);
  const uniqueIds = new Set(candidateIds);
  if (candidateIds.length !== uniqueIds.size) {
    console.warn(`[candidateUtils] Warning: Potential duplicate candidate IDs detected for role ${roleId}:`, candidateIds);
  }

  // Check for candidates with same name but different IDs
  const candidateNames = sortedCandidates.map(c => c.candidate_name);
  const uniqueNames = new Set(candidateNames);
  if (candidateNames.length !== uniqueNames.size) {
    console.warn(`[candidateUtils] Warning: Potential duplicate candidate names detected for role ${roleId}:`, candidateNames);
  }
  
  return {
    first: sortedCandidates[0] || null,
    second: sortedCandidates[1] || null,
    third: sortedCandidates[2] || null
  };
};

export const formatLinkedInLink = (url: string) => {
  return React.createElement(
    'a',
    {
      href: url,
      target: "_blank",
      rel: "noopener noreferrer",
      className: "text-purple-500 hover:underline inline-flex items-center gap-1"
    },
    "Link",
    React.createElement(ExternalLink, { size: 14 })
  );
};

// New utility function to validate candidate data integrity
export const validateCandidateDataIntegrity = (candidates: Candidate[], context: string = '') => {
  const issues = [];
  
  // Check for duplicate candidate_ids
  const candidateIds = candidates.map(c => c.candidate_id).filter(id => id !== undefined);
  const uniqueIds = new Set(candidateIds);
  if (candidateIds.length !== uniqueIds.size) {
    issues.push(`Duplicate candidate_id values detected: ${candidateIds.join(', ')}`);
  }

  // Check for missing required fields
  candidates.forEach((candidate, index) => {
    if (!candidate.candidate_name || candidate.candidate_name.trim() === '') {
      issues.push(`Candidate at index ${index} missing name`);
    }
    if (!candidate.matchPercentage && candidate.matchPercentage !== 0) {
      issues.push(`Candidate ${candidate.candidate_name} missing match percentage`);
    }
    if (!candidate.roleTitle || candidate.roleTitle.trim() === '') {
      issues.push(`Candidate ${candidate.candidate_name} missing role title`);
    }
  });

  if (issues.length > 0) {
    console.warn(`[candidateUtils] Data integrity issues found${context ? ` in ${context}` : ''}:`, issues);
  } else {
    console.log(`[candidateUtils] Data integrity validation passed${context ? ` for ${context}` : ''}`);
  }

  return {
    isValid: issues.length === 0,
    issues
  };
};
